import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material"
import { ReactElement, useEffect, useRef, useState } from "react"
import { StyledMuiFieldLabel } from "./muiFields/MuiFIeldLabel"
import { useFormikContext } from "formik"
import { ISolution } from "../models/Solution"
import { HiddenButton } from "./HiddenButton"
import { defaultSensorsFortouchS, ISensor } from "../models/Sensor"
import Drawing from "../Drawing"
import { defaultFormValues } from "../models/Form"
import { scrollbarSX } from "../Form"
import { NextButton } from "./NextButton"
import { PreviousButton } from "./PreviousButton"
import { Separator } from "./Separator"
import { ProductType } from "../models/Product"

export const CopyDefaultSensor = (): ReactElement => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const { values, setFieldValue } = useFormikContext<ISolution>()
  const prevSensorRef = useRef(values.sensor)

  useEffect(() => {
    const prev = prevSensorRef.current
    const curr = values.sensor

    const hasChanged =
      prev.width !== curr.width ||
      prev.height !== curr.height ||
      prev.withSensor !== curr.withSensor ||
      prev.diagonal !== curr.diagonal ||
      prev.thickness !== curr.thickness ||
      JSON.stringify(prev.activeArea) !== JSON.stringify(curr.activeArea) ||
      JSON.stringify(prev.offset) !== JSON.stringify(curr.offset) ||
      JSON.stringify(prev.tail) !== JSON.stringify(curr.tail) ||
      JSON.stringify(prev.cable) !== JSON.stringify(curr.cable) ||
      JSON.stringify(prev.passiveArea) !== JSON.stringify(curr.passiveArea)

    if (hasChanged && prev.name === curr.name) {
      setFieldValue("sensor.name", undefined)
      setFieldValue("product.adminNotes", "")
    }

    prevSensorRef.current = curr
  }, [values.sensor, setFieldValue])

  if (values.product.type !== ProductType.FortouchS && values.product.type !== ProductType.FortouchO) return <></>

  return (
    <>
      <Separator />

      <Grid container justifyContent="center">
        <Grid item sx={{ marginTop: "7px" }} xs={12} md={5}>
          <StyledMuiFieldLabel paired={false} label={"Select default sensor"} />
        </Grid>

        <Grid item xs={12} md={7}>
          <HiddenButton
            label={values.sensor.name ?? ""}
            onClick={() => setDialogOpen(true)}
          />
        </Grid>
      </Grid>
      {dialogOpen && <SelectDialog setDialogOpen={setDialogOpen} />}
    </>
  )
}

const SelectDialog = (props: { setDialogOpen: (op: boolean) => void }) => {
  const { values, setFieldValue } = useFormikContext<ISolution>()
  const [selected, setSelected] = useState<number | undefined>(undefined)

  console.log(selected !== undefined && defaultSensorsFortouchS[selected])

  const defaultVal = structuredClone(defaultFormValues)
  const solution: ISolution = {
    ...defaultVal,
    glass: { ...defaultVal.glass, withGlass: false },
    sensor:
      selected !== undefined
        ? defaultSensorsFortouchS[selected]
        : defaultVal.sensor,
    layers: {
      layersArr:
        selected !== undefined
          ? [
              {
                order: 0,
                name: "Sensor",
                id: "SN",
                thickness: defaultSensorsFortouchS[selected].thickness,
              },
            ]
          : [],
      totalThicknes:
        selected !== undefined
          ? defaultSensorsFortouchS[selected].thickness
          : 0,
    },
  }

  return (
    <Dialog
      sx={{ ".MuiPaper-root": { maxWidth: "100vw", borderRadius: "14px" } }}
      open={true}
      onClose={() => props.setDialogOpen(false)}
    >
      <DialogTitle sx={{ backgroundColor: "#f7fbff" }}>
        Select default sensor
      </DialogTitle>
      <DialogContent
        sx={{
          width: "55vw",
          height: "60vh",
          padding: "0",
          borderTop: "1px solid #dfe0f2",
          borderBottom: "1px solid #dfe0f2",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row", height: "100%" }}>
          <Box
            sx={{
              width: "30%",
              borderRight: "1px solid #dfe0f2",
              overflowY: "scroll",
              ...scrollbarSX,
            }}
          >
            {defaultSensorsFortouchS.map((sensor: ISensor, i: number) => (
              <Box
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: i === selected ? "#1976d228" : "#1976d214",
                  },
                  borderBottom: "1px solid #dfe0f2",
                  padding: "15px",
                  backgroundColor: i === selected ? "#1976d228" : "white",
                }}
                onClick={() => setSelected(i)}
              >
                {sensor.name}
              </Box>
            ))}
          </Box>
          <Box sx={{ width: "70%" }}>
            {selected === undefined ? (
              <Box
                height={"100%"}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "#3e3259",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    border: "3px solid #3e3259",
                    borderRadius: "9999px",
                    marginBottom: "20px",
                    padding: "5px",
                    height: "20px",
                    width: "20px",
                    alignItems: "center",
                    justifyContent: "center",
                    display: "flex",
                    fontSize: 24,
                    fontWeight: "bold",
                  }}
                >
                  i
                </Box>
                <Box>Select default sensor to see preview</Box>
              </Box>
            ) : (
              <Drawing
                admin={false}
                renderEmpty={false}
                width={725}
                solutionFinal={solution}
                onlyDrawing={true}
                fonstSizeSet={15}
              />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f7fbff" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "row",
            width: "100%",
            marginInline: "2rem",
          }}
        >
          <PreviousButton
            onClick={() => props.setDialogOpen(false)}
            text="cancel"
          />
          <NextButton
            text="select sensor"
            submit={false}
            onClick={() => {
              props.setDialogOpen(false)
              if (selected !== undefined) {
                setFieldValue("sensor", defaultSensorsFortouchS[selected])
                if(values.product.type === ProductType.FortouchO) {
                  setFieldValue("product.adminNotes", "Need to verify final functionality in terms of glass thickness")
                }
              }
            }}
          />
        </Box>
      </DialogActions>
    </Dialog>
  )
}
